import React, { useState, useRef, useEffect } from 'react';
import { colors, radius } from '../../theme';
import { shrinkPhoto, readScanPhoto, newRequestId } from '../../services/bodyScanService';
import { Button, Notice } from './parts';
import ScanForm from './ScanForm';

// Adding a scan (SPEC screens 04 to 08): a photo of the sheet, looked at by the person before it is sent,
// read, and then checked by the person; or the numbers typed. The photo is not kept by Cipher; it goes to
// OpenAI to be read (told before choosing). Nothing is saved until the person taps Save.
//
// Every photo is its own attempt: only the latest one, while this is open, may show its result. Cancelling,
// choosing another photo or closing ends the attempt, so a photo the person turned away never reaches the
// check screen (Codex review of step 5, M3). A photo is sent only when the person taps "Read this photo".
export default function PhotoScan({ clientId, lastMachine, onSaved, onCancel }) {
  const [step, setStep] = useState('choose'); // choose | preparing | preview | reading | check | failed | typed
  const [photo, setPhoto] = useState(null); // { base64, preview }
  const [reading, setReading] = useState(null);
  const [failure, setFailure] = useState(null);
  const [more, setMore] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const fileRef = useRef(null);
  const attempt = useRef(0);

  useEffect(() => () => { attempt.current += 1; }, []); // closing ends whatever is under way
  const endAttempt = () => { attempt.current += 1; };

  // camera: straight to the camera. Otherwise the phone's own chooser, which offers the camera, the
  // photo library and files, so a screenshot from a scale's app can be chosen (iPhone and Android open
  // only the camera while "capture" is set)
  const choose = (camera = false) => {
    endAttempt();
    const input = fileRef.current;
    if (!input) return;
    if (camera === true) input.setAttribute('capture', 'environment'); else input.removeAttribute('capture');
    input.value = '';
    input.click();
  };
  const fail = (mine, err) => {
    if (attempt.current !== mine) return;
    setFailure({ message: err.message, code: err.code });
    setStep('failed');
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const mine = ++attempt.current;
    setFailure(null);
    setPhoto(null);
    setStep('preparing');
    try {
      const shrunk = await shrinkPhoto(file);
      if (attempt.current !== mine) return;
      setPhoto(shrunk);
      setStep('preview');
    } catch (err) { fail(mine, err); }
  };
  const send = async () => {
    if (!photo) return;
    const mine = ++attempt.current;
    setStep('reading');
    try {
      const { reading: r } = await readScanPhoto(clientId, photo.base64, newRequestId());
      if (attempt.current !== mine) return;
      setReading(r);
      setShowPhoto(false);
      setStep('check');
    } catch (err) { fail(mine, err); }
  };
  const backToStart = () => { endAttempt(); setStep('choose'); };

  const fileInput = (
    <input ref={fileRef} type="file" accept="image/*" onChange={onFile}
      aria-label="Photo of your scan sheet" style={{ display: 'none' }} data-testid="scan-photo-input" />
  );
  const sheetImage = (height) => (
    <img src={photo.preview} alt="Your sheet"
      style={{ width: '100%', maxHeight: height, objectFit: 'contain', background: colors.bg, borderRadius: radius.sm, border: `1px solid ${colors.borderStrong}` }} />
  );

  if (step === 'typed') {
    return <ScanForm clientId={clientId} lastMachine={lastMachine} onSaved={onSaved} onCancel={onCancel} />;
  }
  if (step === 'check') {
    return (
      <>
        {fileInput}
        {photo && (
          <div style={{ marginBottom: 12 }}>
            <Button kind="quiet" onClick={() => setShowPhoto(!showPhoto)} aria-expanded={showPhoto}>{showPhoto ? 'Hide my photo' : 'Show my photo'}</Button>
            {showPhoto && <div style={{ marginTop: 8 }}>{sheetImage(420)}</div>}
          </div>
        )}
        <ScanForm clientId={clientId} lastMachine={lastMachine} prefill={reading} onSaved={onSaved} onCancel={onCancel} onRetake={() => choose()} />
      </>
    );
  }
  if (step === 'preparing' || step === 'reading') {
    return (
      <div role="status" aria-live="polite" style={{ display: 'grid', gap: 16, justifyItems: 'center', padding: '8px 0' }}>
        {step === 'reading' && photo && <img src={photo.preview} alt="Your sheet" style={{ width: 120, height: 160, objectFit: 'contain', borderRadius: radius.sm, border: `1px solid ${colors.borderStrong}` }} />}
        <div style={{ fontSize: 15, fontWeight: 600 }}>{step === 'reading' ? 'Reading your sheet…' : 'Getting your photo ready…'}</div>
        {step === 'reading' && <div style={{ fontSize: 13, color: colors.textMuted }}>This usually takes a few seconds.</div>}
        <Button kind="quiet" onClick={backToStart}>Cancel</Button>
      </div>
    );
  }
  if (step === 'preview' && photo) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        {fileInput}
        {sheetImage(360)}
        <div style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>
          Check the numbers are sharp. Only they are needed: if your name, date of birth or a barcode is on the sheet, you can fold it out of the way and take the photo again.
        </div>
        <Button kind="primary" onClick={send}>Read this photo</Button>
        <Button onClick={() => choose()}>Use another photo</Button>
        <Button kind="quiet" onClick={() => { endAttempt(); setStep('typed'); }}>Type numbers instead</Button>
      </div>
    );
  }
  if (step === 'failed') {
    // Another photo will not help when the day's readings are used up, or the person may not add scans
    const retakeHelps = !['limit', 'not_allowed'].includes(failure?.code);
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        {fileInput}
        <div style={{ fontSize: 15, fontWeight: 600 }}>{failure?.message}</div>
        <div style={{ fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>
          {retakeHelps ? 'No numbers were saved. Take the photo again with the whole sheet flat and in good light, or type the numbers.' : 'No numbers were saved.'}
        </div>
        {retakeHelps && <Button kind="primary" onClick={() => choose()}>Try another photo</Button>}
        <Button kind={retakeHelps ? 'secondary' : 'primary'} onClick={() => setStep('typed')}>Type numbers instead</Button>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {fileInput}
      <div style={{ fontSize: 15, lineHeight: 1.6, color: colors.textMuted }}>
        Take a photo of the printed sheet from any machine, or choose a screenshot of your scale's app. We'll read the weight, body fat and muscle for you to check.
      </div>
      <div style={{ fontSize: 13, color: colors.textMuted }}>The whole sheet, flat, in good light. You'll see the photo before it's sent.</div>
      {lastMachine && <div style={{ fontSize: 12, color: colors.textMuted }}>Use the same machine as last time for the fairest comparison.</div>}
      <Button kind="primary" onClick={() => choose(true)}>Take a photo</Button>
      <Button onClick={() => choose()}>Choose a photo or screenshot</Button>
      <Button kind="quiet" onClick={() => setStep('typed')}>Type numbers instead</Button>
      <Notice compact>
        When you tap Read, the photo is sent to OpenAI to read the numbers. Cipher doesn't keep the photo.{' '}
        <button type="button" onClick={() => setMore(!more)} aria-expanded={more} style={{ background: 'none', border: 'none', color: colors.text, textDecoration: 'underline', padding: 0, cursor: 'pointer', font: 'inherit', minHeight: 24 }}>
          {more ? 'Less' : 'More'}
        </button>
        {more && (
          <span style={{ display: 'block', marginTop: 8 }}>
            OpenAI does not train its models on it. It may keep it for up to 30 days to check for misuse, and longer where
            its safety checks or the law require (
            <a href="https://developers.openai.com/api/docs/guides/your-data" target="_blank" rel="noopener noreferrer" style={{ color: colors.text }}>OpenAI's API data controls</a>).
            Cipher keeps the numbers you check and save, which you and your coach can see, and a count of photo readings
            (who, for whom, when) for the daily limit of 10; counts more than a week old are cleared when the next reading is made.
            Reading a photo is optional: you can always type the numbers.
          </span>
        )}
      </Notice>
    </div>
  );
}
